import { today } from "@repo/helpers";
import { KeyedArray } from "@repo/models";
import { Col, EditableGridPage, Loading } from "@repo/ui";
import { useCallback, useMemo } from "react";

import { useDrugTestApi } from "../../api/drugTestApi";
import { useRepSegmentsApi } from "../../api/repSegmentsApi";
import { SingleOnboardingCompletion } from "../../components/onboardingCompletion";
import { DrugTestsProvider, useDrugTestsContext, useRepContext } from "../../contexts/editableItemContext";
import { useOnboardingContext } from "../../contexts/onboardingContext";
import { useSegmentContext } from "../../contexts/segmentContext";
import { usePermissionsCheck } from "../../helpers/permissions";
import { useEntityId, useUpdateRoute } from "../../helpers/urlHooks";
import { DrugTest } from "../../models";
import DrugTestEditor from "./editor";
import DrugTestGridBasic from "./grid";

function DrugTests(props: Props) {
	return (
		<DrugTestsProvider>
			<DrugTestsInternal {...props} />
		</DrugTestsProvider>
	);
}

function DrugTestsInternal(props: Props) {
	const { repId } = useRepContext();
	const { sessionId } = useOnboardingContext();
	const { segmentId } = useSegmentContext();
	const { overwriteItem, pendingChanges } = useDrugTestsContext();
	const { useRepSegmentsQuery } = useRepSegmentsApi(repId);
	const { useDrugTestQuery } = useDrugTestApi();
	const { data: segments, loading: segmentsLoading } = useRepSegmentsQuery();
	const { data: drugTests, loaded: drugTestsLoaded } = useDrugTestQuery(repId);
	const entityId = useEntityId();
	const updateRoute = useUpdateRoute();

	const { fullAPReadOnly } = usePermissionsCheck();

	const inSegment = useCallback(
		({ onboardingSegmentsId }: DrugTest) => {
			if (!segmentId) return true;
			return onboardingSegmentsId?.includes(segmentId);
		},
		[segmentId],
	);

	const inSessionOrActive = useCallback(
		({ onboardingSessionId }: DrugTest) => {
			if (!sessionId) return true;
			if (sessionId === onboardingSessionId) return true;
			return false;
		},
		[sessionId],
	);

	const filteredDrugTests = useMemo(() => {
		if (!drugTests?.any()) return new KeyedArray<DrugTest>([], (d) => d.drugTestId);
		return drugTests.filter(inSegment).filter(inSessionOrActive);
	}, [drugTests, inSegment, inSessionOrActive]);

	const handleOpen = useCallback(
		(item: DrugTest) => {
			overwriteItem(item);
		},
		[overwriteItem],
	);

	const newItem = useCallback(() => {
		overwriteItem({
			conductedOn: today(),
			representativeId: repId,
			exceptionRequested: false,
			onboardingSegmentsId: segments ? segments.map((s) => s.id) : [],
		} as DrugTest);
	}, [overwriteItem, repId, segments]);

	return (
		<Loading loading={segmentsLoading}>
			<Col md={12} tag="section" id="onboarding">
				<DrugTestCompletion />
			</Col>
			<EditableGridPage<DrugTest>
				id="DrugTestsContainer"
				items={filteredDrugTests}
				keyProperty="drugTestId"
				Editor={DrugTestEditor}
				Grid={DrugTestGridBasic}
				openItem={handleOpen}
				pendingChanges={pendingChanges}
				newItem={newItem}
				itemsLoaded={drugTestsLoaded}
				readOnly={props.readOnly}
				entityId={entityId}
				updateRoute={updateRoute}
				fullReadOnly={fullAPReadOnly}
			/>
		</Loading>
	);
}

function DrugTestCompletion() {
	return <SingleOnboardingCompletion req={"drugTest"} depthToDisplay={0} />;
}

export default DrugTests;

interface Props {
	readOnly?: boolean;
}
